import { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { auth, db } from '../config/firebase';

const isNative = Capacitor.isNativePlatform();
const googleProvider = new GoogleAuthProvider();
const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

async function ensureUserDoc(user) {
  const userDocRef = doc(db, 'users', user.uid);
  const snap = await getDoc(userDocRef);
  if (!snap.exists()) {
    await setDoc(userDocRef, {
      profile: {
        email: user.email,
        name: user.displayName || '',
        createdAt: new Date().toISOString(),
      },
      subscription: { status: 'inactive', stripeCustomerId: null, currentPeriodEnd: null },
    });
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, name) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', cred.user.uid), {
      profile: { email, name, createdAt: new Date().toISOString() },
      subscription: { status: 'inactive', stripeCustomerId: null, currentPeriodEnd: null },
    });
    return cred;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // FIX 2026-03-31: revert to documented pattern — await result, extract Google OAuth idToken, bridge to web SDK
  async function signInWithGoogle() {
    if (isNative) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();
      const credential = GoogleAuthProvider.credential(result.credential?.idToken);
      const cred = await signInWithCredential(auth, credential);
      await ensureUserDoc(cred.user);
      return cred;
    } else {
      const cred = await signInWithPopup(auth, googleProvider);
      await ensureUserDoc(cred.user);
      return cred;
    }
  }

  function logout() {
    if (isNative) {
      import('@capacitor-firebase/authentication').then(({ FirebaseAuthentication }) => {
        FirebaseAuthentication.signOut();
      });
    }
    return signOut(auth);
  }

  async function hasVehicles() {
    if (!auth.currentUser) return false;
    const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (!snap.exists()) return false;
    const data = snap.data();
    return data.vehicles && Object.keys(data.vehicles).length > 0;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    const timeout = setTimeout(() => setLoading(false), 5000);
    return () => { unsubscribe(); clearTimeout(timeout); };
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    signInWithGoogle,
    logout,
    hasVehicles,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
